<template>
    <component :is="tag" ref="container">
        <slot></slot>
    </component>
</template>

<script>
import { insertAfter, insertBefore } from 'services/utils/domUtils';

export default {
    name: 'Draggable',
    data() {
        return {
            mouseDown: false,
            dragging: false,
            dragStartOffset: null,
            dragPosition: null,
            previousDragPosition: null,
            initialDragPosition: null,
            directionY: null,
            directionX: null,
            scrollInterval: null
        };
    },
    props: {
        tag: {
            type: String,
            default: 'ul'
        },
        handle: {
            type: String,
            required: true
        },
        scrollContainer: {
            type: String,
            default: 'parent',
            validate: (value) => ['parent', 'self'].includes(value)
        },
        scrollSpeed: {
            type: Number,
            default: 2
        },
        scrollSensitivity: {
            type: Number,
            default: 100
        },
        // scroll again after x ms
        scrollAfter: {
            type: Number,
            default: 1
        },
        axis: {
            type: String,
            default: 'xy',
            validate: (value) => ['x', 'y', 'xy'].includes(value)
        },
        value: {
            type: Array,
            default: () => ([])
        }
    },
    dragClone: null,
    draggedItem: null,
    oldIndex: null,
    newIndex: null,
    methods: {
        getHandles() {
            return Array.from(this.$refs.container.querySelectorAll('.draggable-handle'));
        },
        getScrollContainer() {
            const { container } = this.$refs;

            return this.scrollContainer === 'parent' ? container.parentElement : container;
        },
        onListChange(newList) {
            this.$emit('input', newList);
        },
        onMouseDown(e) {
            if (e.target.closest(this.handle)) {
                this.mouseDown = true;
            }
        },
        onDragStart(e) {
            const { container } = this.$refs;
            const handle = e.target.closest('.draggable-handle');
            const handleIndex = this.getHandles().indexOf(handle);
            const listItem = this.$slots.default[handleIndex].elm;
            const [listItemClientRect] = listItem.getClientRects();
            this.dragging = true;
            this.dragStartOffset = {
                x: e.pageX - listItemClientRect.x,
                y: e.pageY - listItemClientRect.y
            };
            this.initialDragPosition = {
                x: e.pageX,
                y: e.pageY
            };
            this.$options.oldIndex = handleIndex;
            this.$options.newIndex = handleIndex;
            this.previousDragPosition = this.initialDragPosition;
            this.$options.draggedItem = listItem;
            this.$options.dragClone = listItem.cloneNode(true);
            this.$options.draggedItem.classList.add('sortable-ghost');
            this.$options.dragClone.style.position = 'fixed';
            this.$options.dragClone.style.width = `${listItem.clientWidth}px`;
            this.$options.dragClone.classList.add('sortable-drag');
            this.$options.dragClone.style['pointer-events'] = 'none';
            this.$options.oldUserSelect = document.body.style['user-select'];
            this.$options.oldCursor = document.body.style.cursor;
            document.body.style['user-select'] = 'none';
            document.body.style.cursor = 'grabbing';
            container.appendChild(this.$options.dragClone);

            const scrollContainer = this.getScrollContainer();
            const hasScroll = scrollContainer.clientHeight < scrollContainer.scrollHeight;
            if (hasScroll) {
                clearInterval(this.scrollInterval);
                this.scrollInterval = setInterval(() => this.handleScrollContainer(scrollContainer), this.scrollAfter);
            }
        },
        onMouseMove({
            pageX, pageY, target, directionY = null
        }) {
            const { container } = this.$refs;

            if (this.mouseDown && !this.dragging) {
                this.onDragStart({ pageX, pageY, target });
            }

            if (!this.dragging) return;

            const moveXAxis = this.axis.includes('x');
            const moveYAxis = this.axis.includes('y');
            this.dragPosition = {
                x: moveXAxis ? pageX : this.initialDragPosition.x,
                y: moveYAxis ? pageY : this.initialDragPosition.y
            };

            const [containerClientRect] = container.getClientRects();

            if (this.dragPosition.y - this.dragStartOffset.y < containerClientRect.top) {
                this.dragPosition.y = containerClientRect.top + this.dragStartOffset.y;
            }

            if (this.dragPosition.y + this.$options.draggedItem.clientHeight - this.dragStartOffset.y
                > containerClientRect.bottom) {
                this.dragPosition.y = containerClientRect.bottom
                    - this.$options.draggedItem.clientHeight + this.dragStartOffset.y;
            }

            if (this.dragPosition.y !== this.previousDragPosition.y) {
                this.directionY = this.dragPosition.y > this.previousDragPosition.y ? 'down' : 'up';
            } else if (directionY) {
                this.directionY = directionY;
            }

            if (this.dragPosition.x !== this.previousDragPosition.x) {
                this.directionX = this.dragPosition.x > this.previousDragPosition.x ? 'right' : 'left';
            } else {
                this.directionX = null;
            }

            this.previousDragPosition = this.dragPosition;

            const left = this.dragPosition.x - this.dragStartOffset.x;
            const top = this.dragPosition.y - this.dragStartOffset.y;
            this.$options.dragClone.style.left = `${left}px`;
            this.$options.dragClone.style.top = `${top}px`;
        },
        onMouseUp() {
            const { container } = this.$refs;
            this.mouseDown = false;

            if (!this.dragging) return;

            clearInterval(this.scrollInterval);
            const containerChildren = Array.from(this.$refs.container.childNodes);
            this.$options.newIndex = containerChildren.indexOf(this.$options.draggedItem);
            this.dragging = false;
            this.dragPosition = null;
            this.directionX = null;
            this.directionY = null;
            this.previousDragPosition = null;
            this.$options.dragClone.remove();
            this.$options.dragClone = null;
            this.$options.draggedItem.classList.remove('sortable-ghost');
            document.body.style['user-select'] = this.$options.oldUserSelect;
            document.body.style.cursor = this.$options.oldCursor;

            if (this.$options.newIndex !== this.$options.oldIndex) {
                const childInOldIndex = Array.from(container.childNodes)[this.$options.oldIndex];

                if (childInOldIndex) {
                    insertBefore(this.$options.draggedItem, childInOldIndex);
                } else {
                    container.appendChild(this.$options.draggedItem);
                }

                this.onDragChanged();
            }

            this.$options.draggedItem = null;
            this.$options.newIndex = null;
            this.$options.oldIndex = null;
        },
        onDragChanged() {
            const { newIndex, oldIndex } = this.$options;
            const oldIndexValue = this.value[oldIndex];
            const newValue = [...this.value];
            newValue.splice(oldIndex, 1);
            newValue.splice(newIndex, 0, oldIndexValue);
            this.$emit('input', newValue);
            this.$emit('change', {
                newIndex,
                oldIndex,
                itemData: newValue[newIndex]
            });
        },
        handleScrollContainer(scrollContainer) {
            const [containerClientRect] = scrollContainer.getClientRects();

            if (containerClientRect.bottom - this.dragPosition.y <= this.scrollSensitivity) {
                scrollContainer.scrollTo({ top: scrollContainer.scrollTop + this.scrollSpeed });
                this.onMouseMove({
                    pageX: this.dragPosition.x,
                    pageY: this.dragPosition.y,
                    directionY: 'down'
                });
            }

            if (this.dragPosition.y - containerClientRect.top <= this.scrollSensitivity) {
                scrollContainer.scrollTo({ top: scrollContainer.scrollTop - this.scrollSpeed });
                this.onMouseMove({
                    pageX: this.dragPosition.x,
                    pageY: this.dragPosition.y,
                    directionY: 'up'
                });
            }
        },
        onMouseEnterDropzone(e) {
            const dropzone = e.currentTarget;

            if (!this.directionY || !this.dragging || this.$options.draggedItem === dropzone) return;

            if (this.directionY === 'down' && dropzone.nextSibling !== this.$options.draggedItem) {
                insertAfter(this.$options.draggedItem, dropzone);
            }

            if (this.directionY === 'up' && this.$options.draggedItem.nextSibling !== dropzone) {
                insertBefore(this.$options.draggedItem, dropzone);
            }
        },
        bindHandles() {
            const { container } = this.$refs;
            container.addEventListener('mousedown', this.onMouseDown);
            container.addEventListener('mousemove', this.onMouseMove);
            window.addEventListener('mouseup', this.onMouseUp);
            this.$slots.default
                .forEach((slotItem) => slotItem.elm.addEventListener('mousemove', this.onMouseEnterDropzone));
        },
        clearHandlesBindings() {
            const { container } = this.$refs;
            container.removeEventListener('mousedown', this.onMouseDown);
            container.removeEventListener('mousemove', this.onMouseMove);
            window.removeEventListener('mouseup', this.onMouseUp);
            this.$slots.default
                .forEach((slotItem) => slotItem.elm.removeEventListener('mousemove', this.onMouseEnterDropzone));
        }
    },
    async mounted() {
        await this.$nextTick();
        this.bindHandles();
    },
    beforeDestroy() {
        this.clearHandlesBindings();
    },
    updated() {
        this.clearHandlesBindings();
        this.bindHandles();
    }
};
</script>

<style scoped>

</style>
